import { useState } from 'react';
import { Ban } from 'lucide-react-native';
import { ActivityIndicator, type LayoutChangeEvent, Platform, useColorScheme } from 'react-native';
import { Button, type ButtonProps, styled } from 'tamagui';

import { hit, slopFor } from '@/lib/hit-slop';
import { MICRO_TRANSITION, PRESS_TRANSITION } from '@/theme/motion';
import { useShadow } from '@/theme/shadow';
import { borderWidth, darkColors, lightColors } from '@/theme/tokens.generated';

// Buttons and inputs share one control row: md 48, sm 40, fixed heights (not
// padding-derived, so a label size can never change the row). Pill radius.
// Primary carries shadow-2 and a 2px border in its own colour; danger is outline-only,
// outline carries a 2px borderStrong edge. Press moves 1px; there is no scale,
// no spring and no hover or press shadow change.
export const CONTROL_HEIGHT = { md: 48, sm: 40 } as const;

// Tamagui forwards `data-*` props, not RN's `dataSet`, so the web hit area
// attribute is spelled out here next to the native hitSlop.
const smallSlop = slopFor(CONTROL_HEIGHT.sm);
const smallButtonHit = { hitSlop: hit({ vertical: smallSlop }).hitSlop, 'data-hit-v': String(smallSlop) };

const StyledButton = styled(Button, {
  name: 'PrimaryButton',
  alignSelf: 'center',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$primary',
  borderColor: '$primary',
  borderWidth: borderWidth.control,
  borderRadius: '$full',
  color: '$colorOnAction',
  fontFamily: '$body',
  fontWeight: '600',
  paddingVertical: 0,

  // Hover/press change colour only (and 1px of travel on press). `transition`
  // sits inside the pseudo-styles: a top-level raw CSS string crashes Tamagui.
  hoverStyle: {
    backgroundColor: '$primaryHover',
    borderColor: '$primaryHover',
    transition: MICRO_TRANSITION,
  },
  pressStyle: {
    backgroundColor: '$primaryActive',
    borderColor: '$primaryActive',
    y: 1,
    transition: PRESS_TRANSITION,
  },
  // Keyboard focus: @tamagui/button's own focusVisibleStyle draws a 2px outline
  // in $outlineColor, which the theme defines.
  disabledStyle: {
    opacity: 0.5,
    pointerEvents: 'none',
  },

  variants: {
    size: {
      md: {
        height: CONTROL_HEIGHT.md,
        fontSize: '$body',
        lineHeight: '$body',
        paddingHorizontal: '$6',
      },
      sm: {
        height: CONTROL_HEIGHT.sm,
        fontSize: '$meta',
        lineHeight: '$meta',
        paddingHorizontal: '$4',
      },
    },
    // Token by intent: destructive actions are always `outlineDanger` (outline +
    // icon, never a filled lookalike of the brand red) in every place they
    // appear. No `primary` entry: omitting `variant` keeps the base.
    variant: {
      // Neutral and danger outlines: quiet actions that must not compete with
      // the one filled primary.
      outline: {
        backgroundColor: 'transparent',
        borderColor: '$borderColorStrong',
        color: '$color',
        hoverStyle: { backgroundColor: '$backgroundHover', borderColor: '$borderColorStrong', color: '$color' },
        pressStyle: {
          backgroundColor: '$backgroundPress',
          borderColor: '$borderColorStrong',
          color: '$color',
          y: 1,
          transition: PRESS_TRANSITION,
        },
      },
      outlineDanger: {
        backgroundColor: 'transparent',
        borderColor: '$destructive',
        color: '$destructive',
        hoverStyle: { backgroundColor: '$backgroundHover', borderColor: '$destructive', color: '$destructive' },
        pressStyle: {
          backgroundColor: '$backgroundPress',
          borderColor: '$destructive',
          color: '$destructive',
          y: 1,
          transition: PRESS_TRANSITION,
        },
      },
    },
  } as const,

  defaultVariants: {
    size: 'md',
  },
});

// Omit 'variant': @tamagui/button's own ProptsVariant narrows it to "outlined",
// which would collide with ours.
export type PrimaryButtonProps = Omit<ButtonProps, 'variant'> & {
  size?: 'md' | 'sm';
  variant?: 'outline' | 'outlineDanger';
  // Shows a built-in spinner in place of the content and sets aria-busy. The
  // button keeps its colour and its pre-loading width, so nothing jumps when
  // the label swaps. Callers keep passing their own children; they are not
  // rendered while loading.
  loading?: boolean;
};

export function PrimaryButton({ children, loading, variant, size = 'md', ...props }: PrimaryButtonProps) {
  const dark = useColorScheme() === 'dark';
  const colors = dark ? darkColors : lightColors;
  const raised = useShadow(2);
  const [restWidth, setRestWidth] = useState(0);

  // Not in ButtonProps' types, but Tamagui forwards it to the underlying view.
  const onLayoutProps = {
    onLayout: (event: LayoutChangeEvent) => {
      if (!loading) setRestWidth(event.nativeEvent.layout.width);
    },
  } as object;

  const filled = variant === undefined;
  const spinnerColor =
    variant === 'outline'
      ? colors.textPrimary
      : variant === 'outlineDanger'
        ? colors.actionDestructive
        : colors.textOnAction;

  return (
    <StyledButton
      transition="quick"
      variant={variant}
      size={size}
      // sm is 40 high but still gets a 44 touch target on native.
      {...(size === 'sm' ? smallButtonHit : null)}
      // `aria-busy`, not accessibilityState: Tamagui serializes the object to
      // "[object Object]" on web.
      aria-busy={loading || undefined}
      {...(filled ? raised : null)}
      minWidth={loading && restWidth ? restWidth : undefined}
      icon={
        props.icon ??
        (variant === 'outlineDanger' ? (
          <Ban size={16} strokeWidth={1.75} color={colors.actionDestructive} />
        ) : undefined)
      }
      {...onLayoutProps}
      {...props}
    >
      {loading ? (
        <ActivityIndicator
          color={spinnerColor}
          size={Platform.OS === 'ios' ? 'small' : 18}
          accessibilityLabel="Loading"
        />
      ) : (
        children
      )}
    </StyledButton>
  );
}
